import { createSelector } from '@reduxjs/toolkit';

import { formatPaymentMethod } from 'shared/clientUtils';
import { RootState } from 'store/store';
export type PaymentSelectorState = Pick<RootState, 'payment'>;
export type PaymentAndSaleSelectorState = Pick<RootState, 'payment' | 'sale'>;

const paymentMethodType = (store: PaymentSelectorState) => {
  return store.payment.paymentMethodType;
};

export const getUsedPaymentMethod = (store: PaymentSelectorState) => {
  const { paymentMethodType, isPayPalCommerceInvoice, payPalProcessor = '' } = store.payment;
  const formattedPaymentMethod = formatPaymentMethod(paymentMethodType);

  const paymentMethod = isPayPalCommerceInvoice // Format Paypal cases
    ? 'paypal_appconnect_commerce'
    : formattedPaymentMethod === 'pp'
    ? payPalProcessor.toLowerCase() === 'appconnect'
      ? 'paypal_appconnect_non_commerce'
      : 'paypal_ppaam'
    : formattedPaymentMethod;

  return paymentMethod;
};
export const getUsedPaymentType = (store: PaymentSelectorState) => {
  const { isDateScheduled, isAutoPayOn } = store.payment;
  const paymentType = isDateScheduled ? 'schedule' : isAutoPayOn ? 'autopay' : 'payment';

  return paymentType;
};

export const countrySelector = (store: PaymentSelectorState) => {
  return store?.payment?.country;
};

export const isContactDetailsEmailValidSelector = (store: PaymentSelectorState) => {
  return store.payment.isContactDetailsEmailValid;
};

export const isContactDetailsNameValidSelector = (store: PaymentSelectorState) => {
  return store.payment.isContactDetailsNameValid;
};

export const isExternalPaymentMethodSelectedSelector = createSelector(
  paymentMethodType,
  (paymentMethod) => ['paypal_ppaam', 'pp', 'ap', 'venmo', 'affirm_bnpl'].includes(paymentMethod)
);

export const affirmMinTxnLimitRawSelector = (store: PaymentSelectorState) =>
  store?.payment?.affirmMinTxnLimit;

export const affirmMaxTxnLimitRawSelector = (store: PaymentSelectorState) =>
  store?.payment?.affirmMaxTxnLimit;

export const paymentMethodDisplaySelector = createSelector(
  [
    (store: PaymentAndSaleSelectorState) => store.payment,
    (store: PaymentAndSaleSelectorState) => store.sale,
  ],
  (payment, sale): string | undefined => {
    const { lastSuccessfulPaymentResponse, lastSuccessfulSchedulePaymentResponse } = payment || {};

    let maskedAccountNumber: string | undefined;

    if (lastSuccessfulSchedulePaymentResponse?.paymentInfo?.paymentMaskedCard) {
      maskedAccountNumber = String(
        lastSuccessfulSchedulePaymentResponse.paymentInfo.paymentMaskedCard
      );
    } else if (lastSuccessfulPaymentResponse && typeof lastSuccessfulPaymentResponse === 'object') {
      const rawMaskedNumber =
        lastSuccessfulPaymentResponse.maskedAccountNumber ||
        lastSuccessfulPaymentResponse.paymentMethod;
      maskedAccountNumber = rawMaskedNumber ? String(rawMaskedNumber) : undefined;
    } else if (sale?.paymentInfo) {
      const salePaymentInfo = sale.paymentInfo;
      const rawMaskedNumber = salePaymentInfo?.paymentMaskedCard || salePaymentInfo?.paymentMethod;
      maskedAccountNumber = rawMaskedNumber ? String(rawMaskedNumber) : undefined;
    }

    const paymentMethodFromResponse =
      lastSuccessfulPaymentResponse && typeof lastSuccessfulPaymentResponse === 'object'
        ? lastSuccessfulPaymentResponse.paymentMethod
        : undefined;
    const salePaymentMethod = sale?.paymentInfo?.paymentMethod;
    const effectivePaymentMethod = paymentMethodFromResponse || salePaymentMethod;
    const isPayPalCommerce = effectivePaymentMethod === 'PayPalCommerce';
    const isPayPalAsAMark = effectivePaymentMethod === 'PayPal';
    const isVenmo = effectivePaymentMethod === 'Venmo';

    // Handle special cases for PayPal and Venmo variants
    if (isPayPalCommerce || isPayPalAsAMark) {
      return 'PayPal';
    }
    if (isVenmo) {
      return 'Venmo';
    }

    switch (effectivePaymentMethod) {
      case 'bank':
        return `Bank${maskedAccountNumber ? ` ${maskedAccountNumber}` : ''}`;
      case 'ap':
        return 'Apple Pay';
      case 'pp':
        return 'PayPal';
      case 'pbb':
        return 'Pay by Bank';
      default:
        return maskedAccountNumber || undefined;
    }
  }
);
